import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Activity, LogIn, Edit3, Trash2, UserPlus, Lock, FileText, Users,
  BarChart3, Filter, Search, Loader2, Clock, RefreshCw
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_CONFIG = {
  auth: { label: "Autenticacion", icon: LogIn, color: "#3b82f6" },
  cms: { label: "Contenido CMS", icon: Edit3, color: "#06b6d4" },
  blog: { label: "Blog", icon: FileText, color: "#8b5cf6" },
  leads: { label: "Leads", icon: Users, color: "#10b981" },
  users: { label: "Usuarios", icon: UserPlus, color: "#f59e0b" },
};

const ActivityLogView = ({ auth }) => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [search, setSearch] = useState("");
  const authConfig = { auth: { username: auth.username, password: auth.password } };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (categoryFilter) params.append("category", categoryFilter);
      params.append("limit", "100");
      const res = await axios.get(`${API}/admin/activity?${params}`, authConfig);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, [categoryFilter]);

  const filtered = search
    ? logs.filter((l) =>
        l.action?.toLowerCase().includes(search.toLowerCase()) ||
        l.username?.toLowerCase().includes(search.toLowerCase()) ||
        l.details?.toLowerCase().includes(search.toLowerCase())
      )
    : logs;

  const formatTime = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return "Hace un momento";
    if (diff < 3600000) return `Hace ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Hace ${Math.floor(diff / 3600000)}h`;
    return d.toLocaleDateString("es", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white mb-1">Log de Actividad</h1>
        <p className="text-[#94a3b8] text-sm">Registro de todas las acciones realizadas en el panel ({total} registros)</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por accion, usuario..."
            className="w-full bg-[#0b1221] border border-white/10 pl-10 pr-4 py-2.5 text-white text-sm placeholder:text-[#94a3b8]/50 focus:border-[#3b82f6] focus:outline-none transition-colors"
            data-testid="activity-search"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setCategoryFilter("")}
            className={`px-3 py-2 text-xs font-medium border transition-colors ${
              !categoryFilter ? "bg-[#3b82f6]/10 border-[#3b82f6]/30 text-[#3b82f6]" : "border-white/10 text-[#94a3b8] hover:text-white"
            }`}
            data-testid="filter-all"
          >
            Todos
          </button>
          {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
            <button
              key={key}
              onClick={() => setCategoryFilter(key)}
              className={`px-3 py-2 text-xs font-medium border transition-colors flex items-center gap-1.5 ${
                categoryFilter === key ? "border-white/30 text-white" : "border-white/10 text-[#94a3b8] hover:text-white"
              }`}
              style={categoryFilter === key ? { backgroundColor: `${cfg.color}15`, borderColor: `${cfg.color}40`, color: cfg.color } : {}}
              data-testid={`filter-${key}`}
            >
              <cfg.icon className="w-3 h-3" /> {cfg.label}
            </button>
          ))}
          <button
            onClick={fetchLogs}
            className="px-3 py-2 text-xs font-medium border border-white/10 text-[#94a3b8] hover:text-white transition-colors"
            data-testid="refresh-logs"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Timeline */}
      <div className="glass-card p-5" data-testid="activity-timeline">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-[#3b82f6] animate-spin" />
          </div>
        ) : filtered.length > 0 ? (
          <div className="space-y-0">
            {filtered.map((log, i) => {
              const cfg = CATEGORY_CONFIG[log.category] || CATEGORY_CONFIG.auth;
              const LogIcon = cfg.icon;
              return (
                <motion.div
                  key={`${log.timestamp}-${i}`}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className="flex gap-4 group"
                  data-testid={`activity-item-${i}`}
                >
                  {/* Timeline line */}
                  <div className="flex flex-col items-center">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 z-10"
                      style={{ backgroundColor: `${cfg.color}15` }}
                    >
                      <LogIcon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
                    </div>
                    {i < filtered.length - 1 && (
                      <div className="w-px flex-1 bg-white/5 min-h-[24px]" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="pb-5 flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium">{log.action}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className="text-[10px] font-medium px-1.5 py-0.5" style={{ backgroundColor: `${cfg.color}15`, color: cfg.color }}>
                            {cfg.label}
                          </span>
                          <span className="text-[#94a3b8] text-xs">por <span className="text-white font-medium">{log.username}</span></span>
                        </div>
                        {log.details && (
                          <p className="text-[#94a3b8] text-xs mt-1">{log.details}</p>
                        )}
                      </div>
                      <span className="text-[#94a3b8] text-[10px] flex items-center gap-1 flex-shrink-0 whitespace-nowrap">
                        <Clock className="w-3 h-3" /> {formatTime(log.timestamp)}
                      </span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <Activity className="w-12 h-12 text-[#3b82f6]/20 mx-auto mb-3" />
            <p className="text-[#94a3b8]">Sin actividad registrada</p>
            <p className="text-[#94a3b8]/50 text-xs mt-1">Las acciones del panel apareceran aqui</p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ActivityLogView;
